import React, { createContext, useContext } from 'react';
import { styled } from 'styled-components';
import { CompactInput, Input } from 'src/components/widgets/input';
import { TextButton } from 'src/components/widgets/buttons/textButton';
import { useIsSceneLook } from './propertiesPanelLook';
import { panel } from './panelTheme';

// LA RIGA DI UN PARAMETRO — nome a sinistra, controllo a destra, un rigo solo.
//
// NEL VESTITO DELLA SCENA la colonna dei nomi è FISSA e non metà riga. Con `1fr 1fr` ogni riga
// decide da sé dove comincia il proprio controllo, e in una colonna di dieci parametri i dieci
// controlli partono da dieci x diverse appena un nome è più lungo degli altri. Fissandola, la x
// è una sola: è quella che allinea i campi, e sotto di essa cede lo slider — mai il numero.
//
// E i tratteggi fra una riga e l'altra spariscono: separano cose che non hanno bisogno di essere
// separate — a dividere i parametri ci sono già le card — e in una colonna stretta diventano una
// grata.
// ESPORTATO PERCHÉ LA RIGA DI UN CANALE DEVE POTERLO SPEGNERE: là il nome ce l'ha già il canale,
// e una griglia a due colonne dentro una colonna sola la spezzerebbe in due. Chi lo nomina da
// fuori lo fa per toglierlo di mezzo, non per ridichiararne lo stile.
// UNA RIGA SPENTA SI VEDE CHE È SPENTA, e si vede TUTTA: nome compreso. Prima lo diceva solo il
// controllo — un interruttore a metà opacità, restando arancione — e l'arancione in questo
// pannello vuol dire «tocca qui»: una riga che non vale più sembrava una riga da usare.
//
// SI SPEGNE LA RIGA, NON IL CONTROLLO DENTRO: due opacità annidate si moltiplicano, e a forza di
// sovrapporle la riga sparisce invece di spegnersi. I controlli, dal canto loro, perdono il
// colore (`grayscale`) invece di sbiadire — così il velo è uno solo e l'arancione se ne va.
export const PropertiesPanelPropertyContainer = styled.div<{ $scene?: boolean; $narrow?: boolean; $lone?: boolean; $disabled?: boolean }>`
    opacity: ${(props) => (props.$disabled ? 0.5 : 1)};

    display: grid;

    /* DOVE I CAMPI SONO TRE, LA COLONNA DEI NOMI SI STRINGE. Ottantanove pixel sono la misura
       giusta per un nome accanto a UN controllo — «Shadow opacity» ci sta intero — ma davanti a
       una terna sono ottantanove pixel tolti a tre campi che devono starci in fila: restavano
       cinquantacinque per campo, cioè quattordici di testo dopo la lettera dell'asse e lo
       stepper, e «0.00» non ci stava più. Cinquantasei è la misura del mockup, tarata su
       «Rotation»: il nome ci sta ancora, e alla terna tornano trentatré pixel.
       LA SCELTA È DEL CONTROLLO, non della card: è il controllo che sa quanti valori porta.

       E DOVE IL CONTROLLO SI MISURA DA SÉ, IL NOME PRENDE QUELLO CHE RESTA (\`lone\`, il
       \`.chan.lone.free\` del mockup). Un gruppo di lettere non ha una cifra da incolonnare con le
       cifre delle altre righe: sta a destra largo quanto è, e il nome si prende il resto. È così
       che «Texture channel» si legge su un rigo solo anche dentro il corpo di un canale, dove la
       colonna dei nomi è fissa. */
    grid-template-columns: ${(props) =>
        props.$scene ? (props.$lone ? 'minmax(0, 1fr) auto' : props.$narrow ? '56px minmax(0, 1fr)' : '89px minmax(0, 1fr)') : '1fr 1fr'};
    align-items: center;
    gap: ${(props) => (props.$scene ? (props.$narrow ? '8px' : '10px') : '12px')};
    font-family: ${(props) => props.theme.app.font};

    /* NEL VESTITO DELLA SCENA LA RIGA NON PORTA IL PROPRIO SPAZIO: lo dà il corpo della card, con
       un gap che vale per tutte le sue specie di riga. Sommandoli, il passo era la somma di due
       numeri decisi in due file diversi. Resta l'altezza minima, che è quella di un campo: una
       riga con dentro solo una spunta non deve accorciarsi rispetto alle vicine. */
    min-height: ${(props) => (props.$scene ? '26px' : 'auto')};
    padding: ${(props) => (props.$scene ? '0' : '6px 0')};
    border-bottom: ${(props) => (props.$scene ? 'none' : `1px dashed ${props.theme.app.borderColor}44`)};

    &:last-child {
        border-bottom: none;
    }

    /* Il nome di un parametro è spento quando accanto ha un controllo che porta il valore: è il
       valore la cosa da leggere, non la sua etichetta. */
    ${(props) =>
        props.$scene &&
        `
        & > label {
            color: ${panel.inkMuted};
        }
    `}
`;

// LA COLONNA DEL LUCCHETTO, PER OGNI RIGA DEL MATERIALE (round 4, pacchetto D; utente, 2026-10-14).
// I canali la avevano già (`propertiesPanelChannel.tsx`, `Dot`): un materiale «instance» erediterà dal
// master tutto tranne ciò che si sblocca, e il lucchetto starà prima del nome di ogni parametro. Ora
// ce l'hanno anche gli interruttori, i campi e gli slot, così il giorno che arriva non si sposta
// niente. Dentro un canale aperto la colonna resta ma il pallino no: quelle righe sono del canale.
//     off    nessuna colonna — oggetti, luci, camere
//     dot    la colonna col pallino — le righe di un materiale
//     blank  la colonna vuota — le righe dentro un canale
export type PropertiesPanelBullets = 'off' | 'dot' | 'blank';
const BulletsContext = createContext<PropertiesPanelBullets>('off');
export const PropertiesPanelBulletsProvider = BulletsContext.Provider;
export const usePropertiesPanelBullets = () => useContext(BulletsContext);

const BulletRow = styled.div`
    display: grid;
    grid-template-columns: 14px minmax(0, 1fr);
    column-gap: 6px;
    align-items: center;
`;

/** Il pallino della colonna del lucchetto: lo stesso per i canali e per le altre righe. */
export const PropertiesPanelBullet = styled.span<{ $blank?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;

    &::before {
        content: '';
        display: ${(props) => (props.$blank ? 'none' : 'block')};
        width: 4px;
        height: 4px;
        border-radius: 50%;
        background: color-mix(in srgb, ${panel.inkMuted} 55%, transparent);
    }
`;

export const PropertiesPanelProperty = (props: { children: React.ReactNode; narrow?: boolean; lone?: boolean; disabled?: boolean }) => {
    const scene = useIsSceneLook();
    const bullets = usePropertiesPanelBullets();

    const row = (
        <PropertiesPanelPropertyContainer
            $scene={scene}
            $narrow={props.narrow}
            $lone={props.lone}
            $disabled={props.disabled}
            data-lone={props.lone || undefined}
        >
            {props.children}
        </PropertiesPanelPropertyContainer>
    );

    if (!scene || bullets === 'off') return row;

    return (
        <BulletRow>
            <PropertiesPanelBullet aria-hidden='true' $blank={bullets === 'blank'} />
            {row}
        </BulletRow>
    );
};

export const PropertiesPanelLabel = styled.label`
    font-size: 12px;
`;

export const PropertiesPanelControl = styled.div`
    display: flex;
    flex-direction: row;
    gap: 10px;
    align-items: center;
    position: relative;
    width: 100%;
`;

export const PropertiesPanelPropertyContent = styled.div`
    grid-column: 1 / span 2;
`;

export { CompactInput };

export const CompactButton = styled(TextButton)`
    font-size: 12px;
    white-space: nowrap;
`;
